import { useCallback, useEffect, useMemo, useState } from "react";
import { RefreshCw } from "lucide-react";
import { api, categoryColor } from "../api";
import { datumDe, fehlerText, zahlDe } from "./verwaltung";

type Stats = Awaited<ReturnType<typeof api.stats>>;
type Audit = Awaited<ReturnType<typeof api.audit>>;

/** Grobe Hausnummer je Million Tokens (Sonnet-Größenordnung), kein Beleg. */
// Nur noch Rückfall, falls der Server die Schätzung je Modell nicht liefert.
const EUR_PER_MTOK_IN = 3;
const EUR_PER_MTOK_OUT = 15;

const WER: Record<string, string> = { agent: "Agent", user: "Du", system: "System", regel: "Regel", rule: "Regel" };
const KATEGORIE = (c: string) => c.charAt(0).toUpperCase() + c.slice(1);

/** Englische und technische Kürzel aus dem Protokoll in Klartext. */
const AKTION: Record<string, string> = {
  move: "Verschoben", copy: "Kopiert", flag: "Als wichtig markiert", markRead: "Als gelesen markiert",
  markSpam: "Als unerwünscht behandelt", setCategory: "Kategorie gesetzt", setPriority: "Dringlichkeit gesetzt",
  draftReply: "Antwort vorgeschrieben", skipLlm: "Ohne Sprachmodell", send: "Gesendet", login: "Angemeldet",
  "login-failed": "Anmeldung fehlgeschlagen", "classify-failed": "Einordnung fehlgeschlagen",
  "correct-category": "Kategorie korrigiert", "draft-created": "Entwurf erstellt", "draft-failed": "Entwurf fehlgeschlagen",
  "imap-connected": "Postfach verbunden", "reject-action": "Vorschlag abgelehnt", "rule-create": "Regel angelegt",
  "rule-delete": "Regel gelöscht", "rule-update": "Regel geändert", "sent-copy-failed": "Kopie in Gesendet fehlgeschlagen",
  undo: "Rückgängig gemacht", approve: "Vorschlag angenommen",
};
const UMLAUTE: Record<string, string> = {
  geloescht: "gelöscht", geaendert: "geändert", nachtraeglich: "nachträglich", auffaellig: "auffällig",
};

/** „ordner-inhalt-verschoben" → „Ordner inhalt verschoben" — lesbar ohne Tabelle aller Kürzel. */
function aktionText(a: string): string {
  if (AKTION[a]) return AKTION[a]!;
  const t = a.split(/[-_]/).map((w) => UMLAUTE[w] ?? w).join(" ");
  return t.charAt(0).toUpperCase() + t.slice(1);
}

function zeitpunkt(ts: string): string {
  // SQLite liefert UTC ohne Zeitzone.
  const d = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(ts) ? ts : `${ts.replace(" ", "T")}Z`);
  if (Number.isNaN(d.getTime())) return ts;
  return d.toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
}

export function StatsPage() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [audit, setAudit] = useState<Audit | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laedt, setLaedt] = useState(false);
  const [filter, setFilter] = useState<string>("alle");

  const laden = useCallback(async () => {
    setLaedt(true);
    setFehler(null);
    try {
      const [s, a] = await Promise.all([api.stats(), api.audit(120)]);
      setStats(s);
      setAudit(a);
    } catch (e) {
      setFehler(fehlerText(e));
    } finally {
      setLaedt(false);
    }
  }, []);
  useEffect(() => { void laden(); }, [laden]);

  const akteure = useMemo(() => [...new Set((audit ?? []).map((a) => a.actor))], [audit]);
  const sichtbar = useMemo(() => (audit ?? []).filter((a) => filter === "alle" || a.actor === filter), [audit, filter]);

  const kopf = (
    <header className="pane-head">
      <span className="pane-title">Auswertung</span>
      <div className="spacer" />
      <span className="msg-date">letzte 30 Tage</span>
      <button className="icon-btn" onClick={() => void laden()} disabled={laedt} aria-label="Neu laden" title="Neu laden">
        <RefreshCw aria-hidden="true" />
      </button>
    </header>
  );

  if (!stats) {
    return (
      <section className="pane wide" data-active="true">
        {kopf}
        <div className="pane-scroll">
          <div className="page">
            {fehler ? (
              <div className="v-leer" data-art="fehler">
                <strong>Die Auswertung ließ sich nicht laden</strong>
                <span>{fehler}</span>
                <button className="btn" onClick={() => void laden()}>Erneut versuchen</button>
              </div>
            ) : <div className="v-laden" role="status">Lade Auswertung …</div>}
          </div>
        </div>
      </section>
    );
  }

  const max = Math.max(1, ...stats.byCategory.map((c) => c.n));
  const total = stats.byCategory.reduce((s, c) => s + c.n, 0);
  const byStatus = (s: string) => stats.actions.find((a) => a.status === s)?.n ?? 0;
  const cost = stats.kosten?.eur ??
    (stats.tokens.tokens_in / 1_000_000) * EUR_PER_MTOK_IN + (stats.tokens.tokens_out / 1_000_000) * EUR_PER_MTOK_OUT;
  const kostenDetail = stats.kosten?.jeModell
    .map((m) => `${m.model}: ${m.eur === null ? "kein Richtwert" : `${zahlDe(m.eur)} €`} (${m.tokensIn.toLocaleString("de-DE")} rein, ${m.tokensOut.toLocaleString("de-DE")} raus)`)
    .join("\n");
  const wartet = byStatus("pending");

  return (
    <section className="pane wide" data-active="true">
      {kopf}

      <div className="pane-scroll">
        <div className="page">
          {fehler && <div className="v-meldung" data-art="fehler" role="alert"><span>Aktualisieren fehlgeschlagen: {fehler}</span></div>}

          <div className="stat-grid">
            <div className="stat">
              <div className="n">{total.toLocaleString("de-DE")}</div>
              <div className="k">Mails eingeordnet</div>
            </div>
            <div className="stat">
              <div className="n">{(byStatus("applied") + byStatus("simulated")).toLocaleString("de-DE")}</div>
              <div className="k">Aktionen ausgeführt{byStatus("simulated") > 0 ? " (inkl. Testbetrieb)" : ""}</div>
            </div>
            <div className="stat" data-warn={wartet > 0}>
              <div className="n">{wartet.toLocaleString("de-DE")}</div>
              <div className="k">Vorschläge warten auf dich</div>
            </div>
            <div className="stat">
              <div className="n">{stats.corrections.n.toLocaleString("de-DE")}</div>
              <div className="k">Korrekturen von dir</div>
            </div>
            <div className="stat" title={kostenDetail || `${stats.tokens.calls.toLocaleString("de-DE")} Modellaufrufe, ${stats.tokens.tokens_in.toLocaleString("de-DE")} Tokens rein, ${stats.tokens.tokens_out.toLocaleString("de-DE")} raus`}>
              <div className="n">{cost < 0.01 ? "< 0,01" : zahlDe(cost)} €</div>
              <div className="k">
                geschätzte Modellkosten (30 Tage)
                {stats.kosten?.unbekannt.length ? <> · ohne {stats.kosten.unbekannt.join(", ")}</> : null}
              </div>
            </div>
          </div>

          <section className="v-abschnitt">
            <h3>Was ankommt</h3>
            <p className="v-abschnitt-lede">
              Wo sich viel sammelt, lohnt eine feste Regel — die kostet nichts und entscheidet immer gleich.
            </p>
            {stats.byCategory.length === 0 ? (
              <div className="v-leer"><span>Noch keine eingeordneten Mails in diesem Zeitraum.</span></div>
            ) : (
              <div>
                {[...stats.byCategory].sort((a, b) => b.n - a.n).map((c) => (
                  <div className="bar-row" key={c.category}>
                    <span className="name">{KATEGORIE(c.category)}</span>
                    <span className="bar-track" aria-hidden="true">
                      <span className="bar-fill" style={{ width: `${(c.n / max) * 100}%`, ["--bar-color" as string]: categoryColor(c.category) }} />
                    </span>
                    <span className="n">{c.n.toLocaleString("de-DE")}</span>
                  </div>
                ))}
              </div>
            )}
          </section>

          {stats.topRules.length > 0 && (
            <section className="v-abschnitt">
              <h3>Regeln, die greifen</h3>
              <p className="v-abschnitt-lede">Regeln ohne Treffer sind Kandidaten zum Löschen.</p>
              <div className="v-tabelle">
                <table className="log">
                  <thead>
                    <tr>
                      <th>Regel</th>
                      <th className="v-num">Treffer</th>
                      <th>Zuletzt</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.topRules.map((r) => (
                      <tr key={r.name}>
                        <td className="v-zelle-titel">{r.name}</td>
                        <td className="v-num">{r.hit_count.toLocaleString("de-DE")}</td>
                        <td>{r.last_hit_at ? datumDe(r.last_hit_at) : <span style={{ color: "var(--ink-3)" }}>nie</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          <section className="v-abschnitt">
            <div className="v-abschnitt-kopf">
              <div>
                <h3>Was der Agent getan hat</h3>
                <p className="v-abschnitt-lede">Jeder Eingriff am Postfach steht hier — auch der, den du rückgängig gemacht hast.</p>
              </div>
              {akteure.length > 1 && (
                <nav className="tabs" aria-label="Nach Urheber filtern">
                  {["alle", ...akteure].map((a) => (
                    <button key={a} className="tab" aria-pressed={filter === a} onClick={() => setFilter(a)}>
                      {a === "alle" ? "Alle" : WER[a] ?? a}
                    </button>
                  ))}
                </nav>
              )}
            </div>
            {sichtbar.length === 0 ? (
              <div className="v-leer"><span>Noch keine Einträge im Protokoll.</span></div>
            ) : (
              <div className="v-tabelle">
                <table className="log v-protokoll">
                  <thead>
                    <tr>
                      <th>Zeitpunkt</th>
                      <th>Wer</th>
                      <th>Was</th>
                      <th>Nachricht</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sichtbar.map((a) => (
                      <tr key={a.id}>
                        <td>{zeitpunkt(a.ts)}</td>
                        <td><span className="v-wer" data-wer={a.actor}>{WER[a.actor] ?? a.actor}</span></td>
                        <td>{aktionText(a.action)}</td>
                        <td style={{ maxWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={a.subject ?? undefined}>
                          {a.subject ?? "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      </div>
    </section>
  );
}
